import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type {
  ConversationTurn,
  ScheduleEventTurn,
  TranscriptTurn,
  VoiceInterfaceViewState,
} from '@frontend/ai-receptionist-ui';
import { base64ToArrayBuffer, convertFloat32ToInt16 } from '../voice-audio.utils';
import {
  getSucceededScheduleActionFromAirClientEvent,
  hasAppointmentId,
  hasBookingSubmissionId,
  hasStartTime,
  isAirClientEventMessage,
  isRouteActionMessage,
  isStreamAudioMessage,
  isTranscriptMessage,
  type StreamAudioMessage,
  type VoiceAIRWebsocketMessage,
} from '../voice-websocket-events';
import { useVoiceActivityDetection } from './use-voice-activity-detection';

const SAMPLE_RATE = 8000; // 8kHz sample rate for PCM audio

type VoiceSessionAction =
  | { type: 'CALL_CONNECTED' }
  | { type: 'TRANSCRIPT_UPDATE'; turns: ConversationTurn[] }
  | { type: 'AI_SPEAKING_CHANGE'; isSpeaking: boolean }
  | { type: 'USER_SPEAKING_CHANGE'; isSpeaking: boolean }
  | { type: 'SESSION_END' }
  | { type: 'RESET' };

const initialVoiceState: VoiceInterfaceViewState = {
  transcripts: [],
  hasStartedCall: false,
  isAISpeaking: false,
  isUserSpeaking: false,
  showYourTurn: false,
  callEnded: false,
};

const reducer = (state: VoiceInterfaceViewState, action: VoiceSessionAction): VoiceInterfaceViewState => {
  switch (action.type) {
    case 'CALL_CONNECTED':
      return {
        ...state,
        hasStartedCall: true,
        transcripts: [],
        callEnded: false,
      };
    case 'TRANSCRIPT_UPDATE':
      return {
        ...state,
        transcripts: [...state.transcripts, ...action.turns],
      };
    case 'AI_SPEAKING_CHANGE':
      return {
        ...state,
        isAISpeaking: action.isSpeaking,
        showYourTurn: !action.isSpeaking, // Show "your turn" when AI finishes
      };
    case 'USER_SPEAKING_CHANGE':
      return {
        ...state,
        isUserSpeaking: action.isSpeaking,
        showYourTurn: action.isSpeaking ? false : state.showYourTurn, // Hide "your turn" when user speaks
      };
    case 'SESSION_END':
      return {
        ...state,
        isAISpeaking: false,
        isUserSpeaking: false,
        showYourTurn: false,
        callEnded: true,
      };
    case 'RESET':
      return initialVoiceState;
    default:
      return state;
  }
};

interface UseAIRVoiceSessionArgs {
  websocketUrl: string;
  disabled?: boolean;
  enableVAD?: boolean;
  silenceThreshold?: number;
  silenceDuration?: number;
  debugMode?: boolean;
  onError?: (error: Error) => void;
  phoneNumber?: string;
}

export const useAIRVoiceSession = ({
  websocketUrl,
  disabled = false,
  enableVAD = true,
  silenceThreshold = 5,
  silenceDuration = 1500,
  debugMode = false,
  onError,
  phoneNumber,
}: UseAIRVoiceSessionArgs) => {
  const [state, dispatch] = useReducer(reducer, initialVoiceState);
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const nextStartTimeRef = useRef(0);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const activeAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const aiSilenceTimeoutRef = useRef<number | null>(null);
  const isAISpeakingRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    audioStreamRef.current = audioStream;
  }, [audioStream]);

  // Helper function to add transcript - must be defined before VAD callbacks
  const addTranscript = useCallback((speaker: 'user' | 'ai', text: string, isPartial = false) => {
    const turn: TranscriptTurn = {
      id: `${Date.now()}-${speaker}`,
      speaker,
      text,
      timestamp: new Date(),
      isPartial,
    };

    dispatch({ type: 'TRANSCRIPT_UPDATE', turns: [turn] });
  }, []);

  const addScheduleEvent = useCallback(
    ({
      actionType,
      schedulingType,
      appointmentId,
      appointmentRequestId,
      startTime,
      timezone,
    }: Omit<ScheduleEventTurn, 'id' | 'type' | 'timestamp' | 'phoneNumber'>) => {
      const turn: ScheduleEventTurn = {
        id: `${Date.now()}-schedule-${actionType}`,
        type: 'schedule_event',
        actionType,
        schedulingType,
        timestamp: new Date(),
        phoneNumber,
        appointmentId,
        appointmentRequestId,
        startTime,
        timezone,
      };

      dispatch({ type: 'TRANSCRIPT_UPDATE', turns: [turn] });
    },
    [phoneNumber]
  );

  const handleSpeechStart = useCallback(() => {
    dispatch({ type: 'USER_SPEAKING_CHANGE', isSpeaking: true });
  }, []);

  const handleSpeechEnd = useCallback(() => {
    dispatch({ type: 'USER_SPEAKING_CHANGE', isSpeaking: false });

    // For testing: Add a mock user transcript when user stops speaking
    if (debugMode) {
      addTranscript('user', `User test message at ${new Date().toLocaleTimeString()}`);
    }
  }, [debugMode, addTranscript]);

  const stopRecording = useCallback(() => {
    // Stop audio stream tracks first
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    // Stop audio processing nodes
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.port.close();
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current.onaudioprocess = null;
      scriptProcessorRef.current = null;
    }

    setIsRecording(false);
    setAudioStream(null);
    audioStreamRef.current = null;
    dispatch({ type: 'USER_SPEAKING_CHANGE', isSpeaking: false });
  }, []);

  const finishSession = useCallback(() => {
    if (isAISpeakingRef.current) {
      isAISpeakingRef.current = false;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    stopRecording();
    dispatch({ type: 'SESSION_END' });
  }, [stopRecording]);

  const handleStreamAudio = useCallback(
    async ({ audioData, sampleRate }: StreamAudioMessage['data']) => {
      if (!audioContextRef.current) {
        return;
      }

      // Track that AI is speaking
      if (!isAISpeakingRef.current) {
        isAISpeakingRef.current = true;
        dispatch({ type: 'AI_SPEAKING_CHANGE', isSpeaking: true });

        // For testing: Add a mock AI transcript when AI starts speaking
        if (debugMode) {
          addTranscript('ai', `AI started speaking at ${new Date().toLocaleTimeString()}`);
        }
      }

      try {
        const arrayBuffer = base64ToArrayBuffer(audioData);
        const int16Data = new Int16Array(arrayBuffer);
        const float32Data = new Float32Array(int16Data.length);

        // Convert Int16 PCM to Float32 for Web Audio API
        for (let i = 0; i < int16Data.length; i++) {
          float32Data[i] = int16Data[i] / 0x7fff;
        }

        const buffer = audioContextRef.current.createBuffer(1, float32Data.length, sampleRate);
        buffer.getChannelData(0).set(float32Data);

        // Resume AudioContext if suspended (required by browsers after user interaction)
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        const sourceNode = audioContextRef.current.createBufferSource();
        sourceNode.buffer = buffer;
        sourceNode.connect(audioContextRef.current.destination);

        // Track this audio source
        activeAudioSourcesRef.current.add(sourceNode);

        // Clear any existing silence timeout when new audio arrives
        if (aiSilenceTimeoutRef.current) {
          window.clearTimeout(aiSilenceTimeoutRef.current);
          aiSilenceTimeoutRef.current = null;
        }

        // When this audio chunk finishes playing
        sourceNode.onended = () => {
          activeAudioSourcesRef.current.delete(sourceNode);

          // If no more audio sources are playing, start silence detection
          if (activeAudioSourcesRef.current.size === 0 && isAISpeakingRef.current) {
            // Wait a bit to see if more audio comes in
            // If no audio arrives in 500ms, consider AI done speaking
            aiSilenceTimeoutRef.current = window.setTimeout(() => {
              if (activeAudioSourcesRef.current.size === 0) {
                isAISpeakingRef.current = false;
                dispatch({ type: 'AI_SPEAKING_CHANGE', isSpeaking: false });

                // For testing: Simulate user response after AI finishes
                if (debugMode) {
                  // Simulate AI responding back
                  addTranscript('ai', `AI ends speaking at ${new Date().toLocaleTimeString()}`);
                }
              }
            }, 500);
          }
        };

        // Schedule playback to avoid gaps
        const startTime = Math.max(audioContextRef.current.currentTime, nextStartTimeRef.current);
        sourceNode.start(startTime);
        nextStartTimeRef.current = startTime + buffer.duration;
      } catch (error) {
        onError?.(new Error('Failed to play audio. Please try again.'));
      }
    },
    [onError, debugMode, addTranscript]
  );

  const connectWebSocket = useCallback(() => {
    try {
      const ws = new WebSocket(websocketUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        dispatch({ type: 'CALL_CONNECTED' });
      };

      ws.onmessage = async (event) => {
        try {
          const message: VoiceAIRWebsocketMessage = JSON.parse(event.data);

          if (isStreamAudioMessage(message)) {
            await handleStreamAudio(message.data);
            return;
          }

          if (isRouteActionMessage(message)) {
            if (message.type === 'fatal') {
              onError?.(new Error(`Fatal error: ${message.data.reason || 'Unknown reason'}`));
            }
            finishSession();
            return;
          }

          if (isAirClientEventMessage(message)) {
            const scheduleAction = getSucceededScheduleActionFromAirClientEvent(message);
            if (scheduleAction) {
              addScheduleEvent({
                actionType: scheduleAction.action_type,
                schedulingType: scheduleAction.scheduling_type,
                timezone: scheduleAction.timezone,
                ...(hasStartTime(scheduleAction) && { startTime: scheduleAction.start_time }),
                ...(hasAppointmentId(scheduleAction) && { appointmentId: scheduleAction.appointment_id }),
                ...(hasBookingSubmissionId(scheduleAction) && {
                  appointmentRequestId: scheduleAction.booking_submission_id,
                }),
              });
            }
            return;
          }

          if (isTranscriptMessage(message)) {
            const speaker = message.transcript.role === 'assistant' ? 'ai' : 'user';
            addTranscript(speaker, message.transcript.text);
            return;
          }
        } catch (error) {
          onError?.(new Error('Failed to process server message. Please try again.'));
        }
      };

      ws.onclose = (event) => {
        if (!event.wasClean) {
          finishSession();
        }
      };

      ws.onerror = () => {
        onError?.(new Error('Failed to establish connection. Please try again.'));
      };
    } catch (error) {
      onError?.(new Error('Failed to establish connection. Please try again.'));
    }
  }, [websocketUrl, onError, handleStreamAudio, finishSession, addTranscript, addScheduleEvent]);

  const startRecording = useCallback(async () => {
    if (disabled) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)({
          sampleRate: SAMPLE_RATE,
        });
      }

      const source = audioContextRef.current.createMediaStreamSource(stream);

      try {
        // Try to use modern AudioWorklet API
        // Import the worklet file - Vite will handle bundling
        const workletUrl = new URL('../processor/audio-processor.worklet.js', import.meta.url).href;
        await audioContextRef.current.audioWorklet.addModule(workletUrl);

        const workletNode = new AudioWorkletNode(audioContextRef.current, 'audio-capture-processor');
        audioWorkletNodeRef.current = workletNode;

        workletNode.port.onmessage = (event) => {
          if (event.data.type === 'audioData') {
            const float32Data = event.data.data as Float32Array;

            // Convert Float32 to Int16 PCM
            const int16Buffer = convertFloat32ToInt16(float32Data);

            // Send to WebSocket if connected
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(int16Buffer.buffer as BufferSource);
            }
          }
        };

        // Connect the audio graph
        source.connect(workletNode);
        // Connect to a muted gain node to keep processing active without audio output
        const gainNode = audioContextRef.current.createGain();
        // Mute the output
        gainNode.gain.value = 0;
        workletNode.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
      } catch {
        // Fallback to ScriptProcessorNode for older browsers
        const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
        scriptProcessorRef.current = processor;

        processor.onaudioprocess = (event) => {
          const inputData = event.inputBuffer.getChannelData(0);

          // Convert Float32 to Int16 PCM
          const int16Buffer = convertFloat32ToInt16(inputData);

          // Send to WebSocket if connected
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(int16Buffer.buffer as BufferSource);
          }
        };

        // Connect the audio graph
        source.connect(processor);
        // Use a muted gain node to avoid hearing yourself
        const gainNode = audioContextRef.current.createGain();
        // Mute the output
        gainNode.gain.value = 0;
        processor.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
      }

      setIsRecording(true);
      setAudioStream(stream);
    } catch (error) {
      onError?.(new Error('Microphone access failed. Please check your browser permissions and try again.'));
    }
  }, [disabled, onError]);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const handleMicButtonPress = useCallback(() => {
    if (wsRef.current) {
      finishSession();
      return;
    }

    connectWebSocket();
    startRecording();
  }, [connectWebSocket, startRecording, finishSession]);

  // Voice Activity Detection for user speech
  useVoiceActivityDetection({
    stream: audioStream,
    audioContext: audioContextRef.current,
    enabled: isRecording && enableVAD,
    silenceThreshold,
    silenceDuration,
    debugMode,
    onSpeechStart: handleSpeechStart,
    onSpeechEnd: handleSpeechEnd,
    onAudioLevel: setAudioLevel,
  });

  // Initialize AudioContext
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (aiSilenceTimeoutRef.current) {
        window.clearTimeout(aiSilenceTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      stopRecording();
    };
  }, [stopRecording]);

  return {
    state,
    isRecording,
    audioLevel,
    silenceThreshold,
    handleMicButtonPress,
    endCall: finishSession,
    resetState,
  };
};
